import { useRef, useState, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Maps exact phrase text → cache key so the backend serves from disk instantly
const PHRASE_CACHE_KEYS = {
  "Let's begin.": "lets_begin",
  "Next question.": "next_question",
  "Thank you.": "thank_you",
  "Understood.": "understood",
  "Good.": "good",
  "Got it.": "got_it",
  "Take your time.": "take_your_time",
  "That completes your interview. Thank you for your time.": "interview_complete",
  "Moving to the next round.": "moving_next_round",
  "Let me repeat the question.": "ill_repeat",
  "No problem at all, let's move on.": "no_problem",
  "Excellent effort! That completes all your interview questions.": "excellent_effort",
  "Welcome back.": "welcome_back",
  "Give me just a moment.": "thinking",
  "I think you may have missed that — let me repeat the question.": "silence_30s",
  "Take your time, there's no rush.": "silence_60s",
  "It seems we may have a technical issue. Are you able to hear me?": "silence_90s",
  "Let's come back to that one — moving on.": "silence_skip",
  "Let me rephrase that.": "rephrase_question",
  "Let me ask it differently.": "ask_differently",
  "Could you walk me through a specific example?": "minimal_probe",
  "That's very thorough — please summarise your main point in one sentence.": "rambling_interrupt",
  "I'm an AI interviewer. Let's continue with your interview.": "ai_disclosure",
  "Let's keep focused on your interview for now.": "focus_redirect",
  "This session is being recorded for evaluation purposes.": "recording_disclosure",
  "I'm here to learn about you — please go ahead and answer the question.": "counter_redirect",
  "Compensation is best discussed directly with the hiring team.": "salary_deflect",
  "It looks like you might be muted — could you check your microphone?": "mute_prompt",
};

function browserSpeak(text) {
  return new Promise((resolve) => {
    try {
      const synth = window.speechSynthesis;
      synth.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.92;
      u.pitch = 1.0;
      u.lang = "en-US";
      const voices = synth.getVoices();
      const preferred =
        voices.find((v) =>
          /Google US English|Samantha|Microsoft Aria|Microsoft Jenny/i.test(v.name)
        ) || voices.find((v) => v.lang?.startsWith("en"));
      if (preferred) u.voice = preferred;
      u.onend = resolve;
      u.onerror = resolve;
      synth.speak(u);
    } catch {
      resolve();
    }
  });
}

export default function useCartesiaTTS() {
  const [speaking, setSpeaking] = useState(false);
  const audioRef = useRef(null);
  const cancelledRef = useRef(false);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.src = "";
      } catch {}
      audioRef.current = null;
    }
    try { window.speechSynthesis.cancel(); } catch {}
    setSpeaking(false);
  }, []);

  const speak = useCallback(
    async (text) => {
      if (!text?.trim()) return;

      cancel();
      cancelledRef.current = false;
      setSpeaking(true);

      const cacheKey = PHRASE_CACHE_KEYS[text] || "";

      try {
        const controller = new AbortController();
        // 5 s hard timeout — if Cartesia hasn't responded, fall back
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const res = await fetch(`${BACKEND_URL}/api/tts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text, cache_key: cacheKey }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!res.ok) throw new Error(`TTS ${res.status}`);
        if (cancelledRef.current) { setSpeaking(false); return; }

        const blob = await res.blob();
        if (cancelledRef.current) { setSpeaking(false); return; }

        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audioRef.current = audio;

        await new Promise((resolve) => {
          audio.onended = () => { URL.revokeObjectURL(url); resolve(); };
          audio.onerror = () => { URL.revokeObjectURL(url); resolve(); };
          audio.play().catch(() => resolve());
        });
      } catch {
        // Silent fallback — browser TTS keeps the interview alive
        if (!cancelledRef.current) {
          await browserSpeak(text);
        }
      } finally {
        if (!cancelledRef.current) setSpeaking(false);
      }
    },
    [cancel]
  );

  return { speak, cancel, speaking };
}
